/** * DropDown.vue **/
<template>
  <div
    v-click-outside="clickOutsideConfig"
    :data-testid="testid"
    class="drop-down-container"
    :class="{ visible: open }"
  >
    <button class="trigger" type="button" @click="toggle">
      <slot name="trigger" />
    </button>

    <transition
      name="expand"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div v-show="open" class="content" :data-testid="testid + '-content'">
        <slot name="content" />
      </div>
    </transition>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import vClickOutside from '@/directives/clickOutside'

defineOptions({ name: 'DropDown' })

const props = withDefaults(
  defineProps<{
    trigger?: boolean
    expanded?: boolean
    height?: string
    clickOutside?: boolean
    testid?: string
  }>(),
  {
    trigger: true,
    height: 'auto',
    clickOutside: true,
    testid: '',
  },
)

const emit = defineEmits<{
  (e: 'toggle', open: boolean): void
}>()

const open = ref(false)

const clickOutsideConfig = computed(() => ({
  handler: tryClose,
  disabled: !props.clickOutside,
}))

watch(
  () => props.expanded,
  (value) => {
    open.value = !!value
  },
)

onMounted(() => {
  open.value = !!props.expanded
})

function toggle(): void {
  if (props.trigger !== false) {
    open.value = !open.value
    emit('toggle', open.value)
  }
}

function tryClose(): void {
  if (props.clickOutside) {
    close()
  }
}

function close(): void {
  open.value = false
}

// script-setup instances are closed; HeaderMenu calls close() via a template ref.
defineExpose({ close })

function enter(el: Element): void {
  const element = el as HTMLElement
  element.style.height = 'auto'
  const height = getComputedStyle(element).height
  element.style.height = '0'
  // eslint-disable-next-line
  window.getComputedStyle(element).height
  setTimeout(() => {
    element.style.height = props.height === 'auto' ? height : props.height
  })
}

function afterEnter(el: Element): void {
  const element = el as HTMLElement
  element.style.height = props.height
}

function leave(el: Element): void {
  const element = el as HTMLElement
  const height = getComputedStyle(element).height
  element.style.height = height
  // eslint-disable-next-line
  window.getComputedStyle(element).height
  setTimeout(() => {
    element.style.height = '0'
  })
}
</script>
<style scoped lang="scss" src="./drop-down.scss"></style>
